import { useCallback } from 'react';
import { useUpdateSqPrefillPreferenceMutation } from './__generated__/useSqPrefillPreference.generated';
import type { SqPastAnswerPrefillPreference } from '../../../__generated__/graphql-base-types';
import { modifyAutoApplyInfo } from '../../../lib/apollo/client';

interface UseSqPrefillPreferenceResult {
    usePastAnswers: boolean;
    /** Returns true on success, false on failure. Callers that don't need the result can use `void`. */
    handleUsePastAnswersChange: (checked: boolean) => Promise<boolean>;
    /** Error from the most recent mutation attempt, cleared on next attempt */
    error: Error | undefined;
}

export const useSqPrefillPreference = (
    serverValue: string | null | undefined,
): UseSqPrefillPreferenceResult => {
    const [updateSqPrefillPreference, { error }] = useUpdateSqPrefillPreferenceMutation();

    // Derive toggle state directly from server value (defaults to true before data loads)
    const usePastAnswers = serverValue !== 'OPT_OUT';

    const handleUsePastAnswersChange = useCallback(
        async (checked: boolean): Promise<boolean> => {
            const newPreference: SqPastAnswerPrefillPreference = checked ? 'OPT_IN' : 'OPT_OUT';

            try {
                await updateSqPrefillPreference({
                    variables: {
                        input: { sqPastAnswerPrefillPreference: newPreference },
                    },
                    optimisticResponse: {
                        updateAutoApply: {
                            __typename: 'UpdateAutoApplyPayload',
                            sqPastAnswerPrefillPreference: newPreference,
                        },
                    },
                    update(cache, { data: mutationData }) {
                        if (!mutationData) return;
                        modifyAutoApplyInfo(cache, {
                            sqPastAnswerPrefillPreference() {
                                return mutationData.updateAutoApply.sqPastAnswerPrefillPreference;
                            },
                        });
                    },
                });
                return true;
            } catch (error) {
                console.error('Failed to update SQ prefill preference:', error);
                return false;
            }
        },
        [updateSqPrefillPreference],
    );

    return { usePastAnswers, handleUsePastAnswersChange, error };
};
